/**
 * PII redaction — runs in the browser, before anything crosses the network.
 *
 * The promise is not "we handle your PII carefully." It is "your identifying
 * details never leave your device" (ADR-016, ADR-024). The strongest protection
 * in transit is not encryption; it is absence.
 *
 * For a career-change product this is not compliance overhead. The user's
 * sharpest fear is their current employer finding out — BRD §7 (FW-031) already
 * says never name the employer. This module makes that mechanical.
 */

// Order matters: emails and URLs before generic word rules, so their internals
// are not partially rewritten first.
const PATTERNS = [
  [/\b[\w.+-]+@[\w-]+\.[\w.]{2,}\b/g, "[email]"],
  [/\bhttps?:\/\/\S+/gi, "[url]"],
  [/\b(?:www\.)?linkedin\.com\/\S+/gi, "[url]"],
  [/\+?\d[\d\s().-]{7,}\d/g, "[phone]"],
  // Street addresses, and whatever town/state/postcode trails them.
  //
  // The first version stopped at the street type, which left the city marooned
  // between two redactions: "1600 Pennsylvania Ave, Springfield 62704" became
  // "[address], Springfield [postcode]". A city is not a direct identifier on
  // its own, but city + job title + tenure narrows a person a long way, and
  // leaving it behind makes the redaction look thorough while it is not.
  //
  // The trailing groups are all optional, so a bare street still matches. This
  // over-redacts occasionally — "500 Market St Redesign" loses "Redesign" — and
  // that is the correct direction to be wrong in (ADR-024: the default is
  // exclusion).
  [/\b\d{1,5}\s+[A-Z][a-z]+\s+(?:St|Street|Ave|Avenue|Rd|Road|Blvd|Lane|Ln|Dr|Drive|Ct|Way)\b\.?(?:,?\s+[A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+)?)?(?:,?\s+[A-Z]{2}\b)?(?:,?\s+\d{5}(?:-\d{4})?)?/g, "[address]"],
  [/\b\d{5}(?:-\d{4})?\b/g, "[postcode]"],
  // Government identifiers, in case a CV carries one
  [/\b\d{3}-\d{2}-\d{4}\b/g, "[id]"],
];

/**
 * Strip direct identifiers from free text.
 * @param {string} text
 * @param {{names?: string[], employers?: string[]}} known
 *        Names and employers detected during extraction, removed by exact match.
 */
export function redactText(text, known = {}) {
  let out = String(text || "");
  for (const [re, tag] of PATTERNS) out = out.replace(re, tag);

  for (const n of known.names || []) {
    if (n && n.length > 2) out = out.replace(new RegExp(escapeRe(n), "gi"), "[name]");
  }
  for (const e of known.employers || []) {
    if (e && e.length > 2) out = out.replace(new RegExp(escapeRe(e), "gi"), "[employer]");
  }
  return out.replace(/[ \t]{2,}/g, " ").trim();
}

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * The ONLY shape permitted to leave the device.
 *
 * Titles, skills, and durations are what the analysis needs. Names, employers,
 * contact details, and dates that pin a person to a place are what it does not.
 * If a field is not on this list it does not go — the default is exclusion, so
 * a new extraction field cannot silently widen what we transmit.
 */
export function buildTransmissionPayload(parsed) {
  return {
    titles: (parsed.titles || []).map((t) => redactText(t, parsed.known)),
    skills: (parsed.skills || []).map((s) => redactText(s, parsed.known)),
    // Durations in months, never start/end dates — "8 years in the role" carries
    // the signal; "Mar 2017 – Nov 2024 at [employer]" identifies a person.
    tenureMonths: parsed.tenureMonths ?? null,
    industry: parsed.industry ?? null,
    // Free text is redacted AND truncated: a prompt long enough to carry a
    // biography is a prompt long enough to carry an identity.
    taskText: redactText((parsed.taskText || "").slice(0, 2000), parsed.known),
  };
}

/** Cheap assertion for tests and dev: does anything obviously identifying survive? */
export function auditPayload(payload) {
  const blob = JSON.stringify(payload);
  const leaks = [];
  if (/@[\w-]+\.[\w.]{2,}/.test(blob)) leaks.push("email");
  if (/https?:\/\//.test(blob)) leaks.push("url");
  if (/\+?\d[\d\s().-]{9,}\d/.test(blob)) leaks.push("phone-like");
  return { clean: leaks.length === 0, leaks };
}
